/**
 * Content for the `/_/voice` Help page.
 *
 * The engine reads one flat word stream and matches every rule stem-to-stem, so
 * inflections and case never matter ("gutters" is "gutter", "making" is "make",
 * "is/was/are" is "be"). That has three consequences the guide has to teach:
 *
 *  1. Facts live inside a context. A context is opened by `begin <name>` or by
 *     one of the natural-language openers below, and only one is open at a
 *     time; a new opener closes the one before it, and `end <name>` closes it
 *     only when the name matches the open span.
 *  2. Only the listed words match. There is no synonym model and no language
 *     model: the value lists here are the wire vocabulary, value for value.
 *  3. Repetition is safe. Inside one span, saying the same field twice keeps the
 *     last value.
 *
 * The generic utterance template — every part optional except the opener:
 *
 *   begin <context> [field value | marker | number unit | free text | tag as <words>] end <context>
 *
 * The value lists — the contexts and the fields inside them, the unit names, the
 * address cues, the per-context markers, and every vocabulary under
 * `vocabularies` — are read out of the generated artifact
 * (`@/lib/generated/vocabulary.json`, emitted by `cmd/gennlp` from the Go tables
 * that own them), so a table change reaches this page by regenerating rather than
 * by editing two files. Nothing is listed twice, and `voiceHelp.test.ts` fails the
 * build when a derived list is empty or disagrees with the artifact.
 *
 * The prose stays hand-authored copy, keyed to the artifact's own context, key or
 * field name, so a name the artifact renames fails loudly here instead of
 * printing a blank list. The natural-language trigger words are why the artifact
 * cannot supply all of this copy: the engine matches stems ("inspect" covers
 * inspection, "pupa" covers pupae), so the words a technician reads are an
 * editorial spelling no table carries.
 */

import vocabulary from "@/lib/generated/vocabulary.json";

export interface HelpContext {
	name: string;
	purpose: string;
	fields: string[];
	example: string;
}

export interface HelpVocabulary {
	context: string;
	key: string;
	values: string[];
	note?: string;
}

export interface HelpTrigger {
	phrase: string;
	fires: string;
	example: string;
}

export interface HelpTrap {
	what: string;
	why: string;
	instead: string;
}

export interface HelpGap {
	area: string;
	status: "wired" | "partial" | "declared-only";
	detail: string;
}

export interface HelpControl {
	label: string;
	does: string;
}

// The artifact is typed with its literal keys, and every lookup below is by a
// name the Go side owns, so each table is widened to a plain map once here.
type EnumKey = { key: string; values: { display: string }[] };
type BooleanKey = { key: string };
type FreeTextSpec = { context: string; key: string };
type NumberTrigger = { context: string; form: string; field: string };
type AddressCue = { trigger: string[] };

const wireContexts: string[] = vocabulary.extract.contexts;
const enumKeys: Record<string, EnumKey[]> = vocabulary.extract.enumKeys;
const booleanKeys: Record<string, BooleanKey[]> =
	vocabulary.extract.booleanKeys;
const freeTextSpecs: FreeTextSpec[] = vocabulary.extract.freeTextSpecs;
const numberTriggers: NumberTrigger[] = vocabulary.extract.numberTriggers;
const unitTable: { display: string }[] = vocabulary.extract.units;
const addressCueTable: AddressCue[] = vocabulary.extract.address.cues;
const catalogProducts: { identity: { stated: string } }[] =
	vocabulary.extract.products;

/**
 * A missing row means this page and the engine have parted. Fail loudly: a
 * silently blank list on the help page is the one outcome nobody notices.
 */
function artifactRow<T>(what: string, row: T | undefined): T {
	if (!row) {
		throw new Error(
			`voiceHelp: the generated vocabulary has no ${what}; regenerate it with \`go run ./cmd/gennlp\``,
		);
	}
	return row;
}

/**
 * The wire spells a multi-word key in camelCase ("waterQuality"); the page
 * spells the words a technician says ("water quality").
 */
function spoken(key: string): string {
	return key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
}

/** The words the engine recognizes for one context key, in wire order. */
function enumValues(context: string, key: string): string[] {
	const row = artifactRow(
		`${context}.${key} enum`,
		(enumKeys[context] ?? []).find((entry) => entry.key === key),
	);
	return row.values.map((value) => value.display);
}

/**
 * Every field the engine reads inside one context, in the order the page lists
 * them: the enums it carries, then its markers, then its free-text captures,
 * then its count words.
 */
function contextFields(context: string): string[] {
	const enums = (enumKeys[context] ?? []).map((entry) => entry.key);
	const markers = (booleanKeys[context] ?? []).map((entry) => entry.key);
	const captures = freeTextSpecs
		.filter((spec) => spec.context === context)
		.map((spec) => spec.key);
	const counts = numberTriggers
		.filter((trigger) => trigger.context === context)
		.map((trigger) => trigger.field);
	return [...enums, ...markers, ...captures, ...counts];
}

/** Purpose and worked example per context. Every other part is the artifact's. */
const contextCopy: Record<string, { purpose: string; example: string }> = {
	access: {
		purpose:
			"What you met at the edge of the property and how you got to the water.",
		example: "begin access there is not a gate end access",
	},
	facilitator: {
		purpose:
			"What keeps the source producing water, or stands in the way of fixing it.",
		example: "begin facilitator water quality is murky period end facilitator",
	},
	feature: {
		purpose:
			"The source itself: what it is, what its water is doing, and how it looks.",
		example: "begin feature water wet clarity heavy end feature",
	},
	inspection: {
		purpose: "What you found in the water on this visit and how you looked.",
		example: "begin inspection density high end inspection",
	},
	mitigator: {
		purpose:
			"Reserved name for the mitigator role: it opens a span, but nothing structured is read inside it — the mitigator note itself is captured by saying mitigator inside an access or safety span.",
		example: "no mitigator",
	},
	safety: {
		purpose:
			"Hazards to you or the crew at the source, and how you worked around them.",
		example:
			"begin safety constraint aggressive animal not blocking mitigator dog was chained up in the back so I worked the front yard",
	},
	site: {
		purpose:
			"The property around the source and the people you dealt with there.",
		example: "begin site land use agricultural end site",
	},
	trap: {
		purpose:
			"A trap you set or collected: its type and lure, when it went out and came back, the sample label, and anything that was wrong with it.",
		example: "begin trap trap type gravid lure hay infusion end trap",
	},
	treatment: {
		purpose: "What you applied, how much of it, and with what equipment.",
		example: "begin treatment product BVA oil amount 4 oz end treatment",
	},
};

/** The nine contexts, in the artifact's order, with the fields read inside. */
export const contexts: HelpContext[] = wireContexts.map((name) => ({
	name,
	...artifactRow(`help copy for the context ${name}`, contextCopy[name]),
	fields: contextFields(name),
}));

/**
 * Natural-language openers and fact phrases. An opener does what `begin <name>`
 * does; a fact phrase is read only inside an open span of its own context.
 * Hand-authored: the engine matches stems, so a phrase is spelled the way a
 * technician says it ("inspection", "pupae"), not the way a table stores it.
 */
export const nlTriggers: HelpTrigger[] = [
	{
		phrase: "green pool",
		fires:
			"Opens a feature span and asserts habitat = Swimming Pool with maintained = false — the source you have not been maintaining. It does not use up its own words, so a size spoken with it is still read.",
		example:
			"there is a green pool; it looks about ten by fifteen by three feet",
	},
	{
		phrase: "make an inspection / made an inspection",
		fires: "Opens an inspection span. No facts of its own.",
		example: "I am making an inspection.",
	},
	{
		phrase: "first through pupae",
		fires:
			"Inside an inspection span: asserts stage = Late. The phrase stands for the whole late band, and it claims its own words so the ordinal is never read as a count.",
		example:
			"I am making an inspection. There is heavy production with mixed stages, first through pupae.",
	},
	{
		phrase: "make a treatment / made a treatment",
		fires: "Opens a treatment span. No facts of its own.",
		example: "I made a treatment today.",
	},
	{
		phrase: "any product name (inside a treatment span)",
		fires:
			"Names the product and takes the amount spoken with it. Each product match starts its own treatment instance, so one sentence naming two products is two treatments, each with its own amount. A product named outside a treatment span is read by nothing.",
		example:
			"I made a treatment with BVA oil, four ounces; VectoMax FG, two ounces.",
	},
	{
		phrase: "fish",
		fires:
			"Inside a treatment span: the product biological control, with the amount allowed to lead it (about eight fish). Inside a feature span the same word is the feature fish marker instead.",
		example: "begin treatment about eight fish end treatment",
	},
	{
		phrase: "tag as <words>",
		fires:
			"Captures a custom tag, at most three words, ending at a comma or at the first clause word. tag this <words>, tag <words> and add tag <words> work the same way. Tags are listed in the Tags pane.",
		example:
			"Begin inspection. Tag this safety. This thing is on fire. Tag this followup.",
	},
];

/** Prose per free-text capture, keyed by the artifact's own context and key. */
const freeTextCopy: Record<string, { fires: string; example: string }> = {
	"access.mitigator": {
		fires:
			"In an access span: starts a capture for access.mitigator. Everything after the trigger becomes the text, up to the first of a begin/end marker, the word period or stop, a pause between utterances, or the end of the transcript.",
		example:
			"begin access constraint locked gate blocking yes mitigator called the number on the gate got code five five two one",
	},
	"safety.mitigator": {
		fires:
			"In a safety span: starts a capture for safety.mitigator, with the same endings as above.",
		example:
			"begin safety constraint aggressive animal not blocking mitigator dog was chained up in the back so I worked the front yard",
	},
	"facilitator.waterQuality": {
		fires:
			"In a facilitator span: starts a capture for facilitator.waterQuality.",
		example: "begin facilitator water quality is murky period end facilitator",
	},
	"facilitator.drainage": {
		fires: "In a facilitator span: starts a capture for facilitator.drainage.",
		example: "begin facilitator drainage is bad stop end facilitator",
	},
	"facilitator.grading": {
		fires: "In a facilitator span: starts a capture for facilitator.grading.",
		example: "begin facilitator grading needs work",
	},
	"inspection.method": {
		fires: "In an inspection span: starts a capture for inspection.method.",
		example:
			"begin inspection method ten dips around the perimeter end inspection",
	},
	"site.contact": {
		fires: "In a site span: starts a capture for site.contact.",
		example:
			"begin site contact the neighbor land use agricultural period end site",
	},
	"treatment.equipment": {
		fires: "In a treatment span: starts a capture for treatment.equipment.",
		example:
			"begin treatment equipment the sprayer amount 5 gallons period amount 2 ounces end treatment",
	},
	"trap.trapType": {
		fires:
			"In a trap span: starts a capture for trap.trapType. Say trap type or just type; the words after it are the type, read against the district's own vocabulary when the note is derived.",
		example: "begin trap trap type gravid end trap",
	},
	"trap.lure": {
		fires:
			"In a trap span: starts a capture for trap.lure — the lure you used.",
		example: "begin trap lure hay infusion end trap",
	},
	"trap.status": {
		fires:
			"In a trap span: starts a capture for trap.status — free prose about the trap's condition.",
		example: "begin trap status pan full lid was loose end trap",
	},
	"trap.sampleLabel": {
		fires:
			"In a trap span: starts a capture for trap.sampleLabel — the words written on the sample. Say sample id or just sample; sample id is matched first, so both words are the one capture.",
		example: "begin trap sample id one zero four two a end trap",
	},
};

/** Free-text captures. The trigger word starts prose; it is not a value list. */
export const freeTextTriggers: HelpTrigger[] = freeTextSpecs.map((spec) => {
	const key = `${spec.context}.${spec.key}`;
	return {
		phrase: spoken(spec.key),
		...artifactRow(`help copy for the capture ${key}`, freeTextCopy[key]),
	};
});

/** Prose per count word, keyed by the artifact's own context and form. */
const countCopy: Record<string, { fires: string; example: string }> = {
	"treatment.amount": {
		fires:
			"In a treatment span: treatment.amount, followed by a count and a unit. Up to two filler words may sit in between (about, around, roughly, approximately, of).",
		example: "begin treatment amount about 15 ounces end treatment",
	},
	"treatment.treated": {
		fires:
			"In a treatment span: treatment.treated, followed by a count and a unit.",
		example: "begin treatment treated three end treatment",
	},
	"feature.quantity": {
		fires:
			"In a feature span: feature.quantity, followed by a count and a unit.",
		example: "begin feature quantity 6 briquets end feature",
	},
};

/** Count and size triggers. Each count word belongs to one context. */
export const countTriggers: HelpTrigger[] = [
	...numberTriggers.map((trigger) => {
		const key = `${trigger.context}.${trigger.form}`;
		return {
			phrase: trigger.form,
			...artifactRow(`help copy for the count word ${key}`, countCopy[key]),
		};
	}),
	{
		phrase: "a product name (or fish) after an amount",
		fires:
			"The product and its amount from the same words: the product name sets treatment.product and the count spoken with it sets treatment.amount.",
		example: "begin treatment product BVA oil amount 4 oz end treatment",
	},
	{
		phrase: "size",
		fires:
			"A size specification: two dimensions are a surface area, three are a volume. A dimension may be named after its number (length or long, width or wide, depth or deep — 20 feet long by 40 feet wide), and the unit is feet or inches or meters; feet is assumed when no unit is spoken.",
		example:
			"begin feature habitat swimming pool water wet size 10 by 15 by 3 feet end feature",
	},
	{
		phrase:
			"a hedge (about, around, approximately, roughly, nearly, almost, maybe)",
		fires:
			"Starts a size specification on its own when the first dimension is the very next word, and marks the result an estimate. about, around, roughly and approximately also mark a count an estimate.",
		example:
			"there is a green pool; it looks about ten by fifteen by three feet",
	},
];

/** Canonical unit names, spelled as the engine's unit table spells them. */
export const units: string[] = unitTable.map((unit) => unit.display);

/**
 * The engine matches a cue stem-to-stem, so the one stem too short to read as
 * the word it stands for is spelled out here.
 */
const cueSpellings: Record<string, string> = { locat: "located" };

/** Phrases that introduce a spoken address. */
export const addressCues: string[] = addressCueTable.map((cue) =>
	cue.trigger.map((stem) => cueSpellings[stem] ?? stem).join(" "),
);

/** Per-context markers, each spoken word asserting its own fact inside that context. */
export const booleanTags: { context: string; tags: string[] }[] =
	wireContexts.map((context) => ({
		context,
		tags: (booleanKeys[context] ?? []).map((marker) => spoken(marker.key)),
	}));

/**
 * Every field that carries a fixed vocabulary, with the words that fill it. The
 * order is this page's; the words are the artifact's, read by context and key,
 * and the notes are copy that explains a gap between the wire and the ontology.
 */
export const vocabularies: HelpVocabulary[] = [
	{
		context: "inspection",
		key: "density",
		values: enumValues("inspection", "density"),
	},
	{
		context: "inspection",
		key: "genus",
		values: enumValues("inspection", "genus"),
	},
	{
		context: "inspection",
		key: "stage",
		values: enumValues("inspection", "stage"),
	},
	{
		context: "site",
		key: "landUse",
		values: enumValues("site", "landUse"),
		note: "The declared label Mixed use is recognized and shown as Mixed Use.",
	},
	{
		context: "safety",
		key: "constraint",
		values: enumValues("safety", "constraint"),
		note: "Chemical Exposure Risk is declared in the ontology but is not recognized yet.",
	},
	{
		context: "feature",
		key: "clarity",
		values: enumValues("feature", "clarity"),
		note: "Matches the declared Organic Content vocabulary exactly.",
	},
	{
		context: "feature",
		key: "flow",
		values: enumValues("feature", "flow"),
		note: "Say slow or fast — the declared spellings slow-flowing and fast-flowing match nothing.",
	},
	{
		context: "feature",
		key: "retained",
		values: enumValues("feature", "retained"),
	},
	{
		context: "feature",
		key: "vegetation",
		values: enumValues("feature", "vegetation"),
		note: "Matches the declared Vegetation Influence vocabulary exactly.",
	},
	{
		context: "feature",
		key: "water",
		values: enumValues("feature", "water"),
	},
	{
		context: "feature",
		key: "habitat",
		values: enumValues("feature", "habitat"),
	},
	{
		context: "feature",
		key: "waterOrigin",
		values: enumValues("feature", "waterOrigin"),
		note: "The field name is also spoken as origin. rain resolves to Rainwater Accumulation and stormwater to Stormwater/Municipal Runoff.",
	},
	{
		context: "treatment",
		key: "product",
		values: enumValues("treatment", "product"),
		note: "Only these spoken forms match, and the stated name is always the one shown. A full product name such as VectoMax FG Biological Larvicide Fine Granules matches nothing. Altosid on its own resolves to Methoprene; Bti has no expansion, so Bacillus thuringiensis israelensis matches nothing. None (also no treatment needed) records that no product was applied. A few entries also accept a second spoken form: Altosid Liquid SR-20, Altosid Liquid SR-5, BVA 2 Larvicide, BVA L2, VectorMax G, VectoMax WSP.",
	},
];

/** What a test will say that the engine does not read the way it sounds. */
export const traps: HelpTrap[] = [
	{
		what: "You opened with something that is not a context name: begin report the source is cracked.",
		why: "The word begin has to be followed immediately by one of the nine context names; any other word opens nothing.",
		instead: "begin feature the source is cracked end feature",
	},
	{
		what: "You closed a span with a different name: begin feature … end site.",
		why: "A closer only takes effect when it names the span that is open. Anything else is ignored and the span stays open.",
		instead:
			"end the name you opened, or simply open the next context — a new opener closes the previous span by itself.",
	},
	{
		what: "You used your own word for a listed one: the gate is secured.",
		why: "Only the listed words match, stem to stem: secured is not a form of locked, so the gate marker is recorded and the lock is not.",
		instead: "Say the listed word: the gate is locked.",
	},
	{
		what: "You put too much between a field and its value: land use is a big agricultural.",
		why: "A field name and its value may sit at most two linking words apart (is, are, was, be, seems, appears, looks …); anything else breaks the pair.",
		instead: "land use is agricultural",
	},
	{
		what: "You named a product in passing: we used Altosid on the last visit.",
		why: "Products are read only inside an open treatment span, and that sentence opens nothing.",
		instead: "I made a treatment with Altosid, two ounces.",
	},
	{
		what: "You gave a feature fact with no context open: no fish.",
		why: "A fact needs its span open. The only things the engine reads bare are the access markers, a size specification, a custom tag and a spoken address, so a feature fact spoken bare is not recorded.",
		instead: "begin feature no fish end feature",
	},
	{
		what: "You used a count word in the wrong context: treated inside a feature span, or quantity inside a treatment span.",
		why: "Each count word belongs to one context — amount and treated to treatment, quantity to feature — and it does not fire anywhere else.",
		instead:
			"Open the matching context, or use the count word that belongs to the span you are in.",
	},
	{
		what: "You dictated a fractional amount: 2.5 ounces, or two point five ounces.",
		why: "A full stop is dropped when the transcript is read and a spoken number stops at its whole part, so both of those record a count of 2 with no unit instead of two and a half.",
		instead:
			"Give the fraction on its own: point five ounces (0.5), point two ounces (0.2).",
	},
	{
		what: "You gave an address without a cue, or after one but not a house number: I am here at the meeting.",
		why: "An address needs one of the four cues, and the next word after the cue has to start with a digit.",
		instead: "I am here at 2345 West Cherry Court.",
	},
	{
		what: "You put a fact inside a free-text note: method ten dips around the perimeter, water wet.",
		why: "A free-text capture is sticky — everything after the trigger is prose until the capture ends, so the words after it are not read as values.",
		instead:
			"End the note with period or stop, or close the span, before your next fact.",
	},
	{
		what: "You used the plain verb: inspect, survey or check on its own.",
		why: "Only make an inspection or made an inspection opens an inspection span; the bare verb is not a trigger.",
		instead: "I am making an inspection.",
	},
	{
		what: "You gave dimensions with nothing to announce them: found twenty by forty.",
		why: "Dimensions are read only after a size word or after a hedge that sits immediately in front of them; a bare pair of numbers is not a size.",
		instead: "size twenty by forty, or it is about twenty by forty.",
	},
	{
		what: "You separated a hedge from its numbers: about 20 containers, or it looks about, ten by fifteen.",
		why: "When the hedge itself announces the size, the first dimension has to start at the very next word.",
		instead: "it looks about ten by fifteen by three feet",
	},
	{
		what: "You spoke a product's full name: VectoMax FG Biological Larvicide Fine Granules, two ounces.",
		why: "Only the listed spoken form is recognized, so the extra words are not part of the product and land between it and its amount: the product is still recorded, and the amount is not.",
		instead: "VectoMax FG, two ounces.",
	},
];

// The notes below count what this page lists, read from the artifact so a table
// that grows or shrinks rewrites its own sentence.
const habitats = enumValues("feature", "habitat");
const waterOrigins = enumValues("feature", "waterOrigin");
const landUses = enumValues("site", "landUse");

/**
 * The honest line between what the engine recognizes today and what the
 * ontology merely declares.
 */
export const gaps: HelpGap[] = [
	{
		area: `terms/habitats/ (${habitats.length} declared) and feature.habitat (${habitats.length} recognized)`,
		status: "wired",
		detail: `You can say any of the ${habitats.length} habitat types today, in a feature span. The declared list and the recognized list are the same set, value for value.`,
	},
	{
		area: `terms/water-origins/ (${waterOrigins.length} declared) and feature.waterOrigin (${waterOrigins.length} recognized)`,
		status: "wired",
		detail: `You can say any of the ${waterOrigins.length} water origins today, in a feature span, under water origin or origin.`,
	},
	{
		area: `terms/water-uses/ (${landUses.length} declared) and site.landUse (${landUses.length} recognized)`,
		status: "wired",
		detail: `You can say any of the ${landUses.length} land uses today, in a site span. The one label that reads differently on the wire is Mixed Use.`,
	},
	{
		area: `treatment product catalog (${catalogProducts.length} products) and None`,
		status: "wired",
		detail:
			"You can say any catalog product inside an open treatment span today, and None for no product applied. These words live in the engine's own catalog rather than in a declared ontology vocabulary.",
	},
	{
		area: "terms/observation-values/ — Organic Content",
		status: "partial",
		detail:
			"You can say Clear, Light and Heavy today as feature.clarity; all three match exactly. Not yet for the rest of this vocabulary: Flow State needs different spellings and Water Connectivity has no path at all.",
	},
	{
		area: "terms/observation-values/ — Vegetation Influence",
		status: "partial",
		detail:
			"You can say None, Incidental, Influential and Constraining today as feature.vegetation; all four match exactly. Not yet for the rest of this vocabulary, as with Organic Content.",
	},
	{
		area: "terms/observation-values/ — Flow State",
		status: "partial",
		detail:
			"You can say flow stagnant, flow slow and flow fast today. The declared spellings slow-flowing and fast-flowing are not yet recognized — say slow or fast.",
	},
	{
		area: "terms/observation-values/ — Water Connectivity",
		status: "declared-only",
		detail:
			"Not yet — this is on the roadmap. Isolated, Locally Connected, System-connected and Unknown have no extraction path; the only trace is an unused type in the frontend API contract.",
	},
	{
		area: "terms/facilitator-types/ (19 declared)",
		status: "partial",
		detail:
			"You can say the facilitator markers today (irrigation, neglect, obstruction, overwatering, vegetation) and dictate prose after drainage, grading and water quality. The 18 other declared type names are not yet recognized — put those observations in a free-text note instead.",
	},
	{
		area: "Safety Constraint — Chemical Exposure Risk",
		status: "partial",
		detail:
			"You can say the other five safety constraints today. Chemical Exposure Risk is declared in the ontology but not yet recognized — not yet, this is on the roadmap.",
	},
	{
		area: "Access Constraint vocabulary (Locked Gate, No Response, Animal Present, Refused Entry, Physical Barrier)",
		status: "partial",
		detail:
			"There is no typed access-constraint value to say. The same situations are recorded today as access markers — locked, answer, refused, trespassing, gate, blocked — so say those; the declared type names are not yet recognized.",
	},
	{
		area: "Frontend Transcript* vocabulary with no consumer (water connectivity, sun exposure, container condition, bank accessibility, stage composition, treatment method, constraint type)",
		status: "declared-only",
		detail:
			"Not yet — this is on the roadmap. The API contract declares these lists but nothing in the engine or this workspace reads them, and two of them disagree with the values the engine actually emits (stage composition declares Mostly early / Mostly late where inspection.stage uses Early / Late).",
	},
	{
		area: "Ontology entities with no extraction path (Signal, Situation, Lead, Assignment, Route, Technician, Suppression, Prevention, Work Order, Compliance, Site Contact, Communication, Mosquito Control Plan, Review)",
		status: "declared-only",
		detail:
			"Not yet — this is on the roadmap. The engine opens nine contexts; these sections have no counterpart, so saying anything about them records nothing.",
	},
];

/** Every control on the workspace, in the order they appear. */
export const uiControls: HelpControl[] = [
	{
		label: "Start recording",
		does: "Clears the transcript, the errors and the last extraction, resets the prompt session, opens the transcription connection and asks for the microphone.",
	},
	{
		label: "Stop recording",
		does: "Stops the microphone and the connection, sets the status to Idle and runs one final extraction pass over everything said.",
	},
	{
		label: "Headset mode",
		does: "Turns spoken prompting on or off; while it is on, the page can read the next prompt aloud and the button shows as active.",
	},
	{
		label: "Manual transcript",
		does: "Opens the text panel, where you can type a transcript yourself to reproduce a known state.",
	},
	{
		label: "Render transcript",
		does: "Renders the typed panel, one utterance per line. Typed lines carry no pause markers, because pauses come from the transcription service.",
	},
	{
		label: "Copy transcript",
		does: "Copies the transcript, one utterance per line, and shows a check mark for a moment.",
	},
	{
		label: "Context badge",
		does: "Shows the context the engine is currently inside, or a dash when no span is open.",
	},
	{
		label: "Status",
		does: "Shows Recording… while the microphone is live and Idle otherwise.",
	},
	{
		label: "Level meter",
		does: "The input level bar with quiet, good and loud zones; aim for the target band in the middle of the range.",
	},
	{
		label: "prompt badge",
		does: "Appears only while recording in headset mode and a pause has just happened, which is when the next prompt is offered.",
	},
];
